/**
 * Container Issues Tab
 *
 * Displays a table of issues for a container with the ability to change status.
 */

import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { AlertCircle, InfoCircle, RefreshCcw01 } from '@untitledui/icons';
import { DialogTrigger, Popover, Button } from 'react-aria-components';
import { Table, TableCard } from '@/components/application/table/table';
import { Badge } from '@/components/base/badges/badges';
import { Dropdown } from '@/components/base/dropdown/dropdown';
import { useContainerIssues, useResetContainerIssues, ACTIVE_STATUSES, type ContainerIssue } from '@/hooks/api/use-container-issues';
import { useIssueTypeLabel } from '@/hooks/api/use-issue-types';
import { ChangeIssueStatusModal } from './modals/change-issue-status-modal';

interface ContainerIssuesTabProps {
    containerId: string;
    containerNumber: string;
}

// Helper to format relative time
const formatTimeAgo = (dateString: string | null): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('it-IT');
};

// Helper to get severity badge color
const getSeverityColor = (severity: string): 'error' | 'warning' | 'brand' | 'gray' => {
    switch (severity.toLowerCase()) {
        case 'critical':
            return 'error';
        case 'high':
            return 'warning';
        case 'medium':
            return 'brand';
        case 'low':
        default:
            return 'gray';
    }
};

// Helper to get status badge color
const getStatusColor = (status: string): 'error' | 'warning' | 'success' | 'brand' | 'gray' => {
    switch (status.toLowerCase()) {
        case 'open':
            return 'error';
        case 'acknowledged':
        case 'in_progress':
            return 'brand';
        case 'pending_info':
        case 'waiting_on_external':
            return 'warning';
        case 'resolved':
            return 'success';
        case 'false_positive':
        case 'wont_fix':
            return 'gray';
        default:
            return 'gray';
    }
};

// Helper to format status label
const formatStatusLabel = (status: string): string => {
    return status
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (l) => l.toUpperCase());
};

// Strip container reference from issue title (redundant on container detail page)
const stripContainerRef = (title: string, containerNumber: string): string => {
    if (!containerNumber) return title;
    const escaped = containerNumber.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return title
        .replace(new RegExp(`:\\s*${escaped}\\s*$`, 'i'), '')
        .replace(new RegExp(`\\s*\\(${escaped}\\)\\s*$`, 'i'), '')
        .trim();
};

export function ContainerIssuesTab({ containerId, containerNumber }: ContainerIssuesTabProps) {
    const getIssueTypeLabel = useIssueTypeLabel();
    const { t } = useTranslation(['pages:containers', 'common']);
    const [searchParams] = useSearchParams();
    const issueTypeFilter = searchParams.get('issue_type') ?? undefined;
    const { data, isLoading } = useContainerIssues(containerId, {
        issueType: issueTypeFilter || undefined,
    });
    const [selectedIssue, setSelectedIssue] = useState<ContainerIssue | null>(null);
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [showResolved, setShowResolved] = useState(false);
    const [showResetConfirm, setShowResetConfirm] = useState(false);
    const resetMutation = useResetContainerIssues();

    const handleChangeStatus = (issue: ContainerIssue) => {
        setSelectedIssue(issue);
        setShowStatusModal(true);
    };

    const handleStatusModalClose = () => {
        setShowStatusModal(false);
        setSelectedIssue(null);
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <p className="text-tertiary">{t('detail.loading', 'Loading...')}</p>
            </div>
        );
    }

    const allIssues = data?.issues || [];
    const resolvedStatuses = ['resolved', 'false_positive', 'wont_fix'];
    const issues = showResolved ? allIssues : allIssues.filter((i) => !resolvedStatuses.includes(i.resolution_status));
    const resolvedCount = allIssues.length - allIssues.filter((i) => !resolvedStatuses.includes(i.resolution_status)).length;

    if (allIssues.length === 0) {
        return (
            <div className="rounded-xl bg-primary p-8 shadow-xs ring-1 ring-secondary text-center">
                <AlertCircle className="size-12 mx-auto mb-4 text-tertiary" />
                <h3 className="text-lg font-medium text-primary mb-2">
                    {t('detail.issues.noIssues', 'No issues')}
                </h3>
                <p className="text-tertiary">
                    {t('detail.issues.noIssuesMessage', 'No issues have been reported for this container')}
                </p>
            </div>
        );
    }

    const activeCount = allIssues.filter((i) => ACTIVE_STATUSES.includes(i.resolution_status)).length;

    const handleReset = () => {
        resetMutation.mutate(containerId, {
            onSuccess: () => setShowResetConfirm(false),
        });
    };

    return (
        <>
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    {activeCount > 0 && (
                        <button
                            type="button"
                            onClick={() => setShowResetConfirm(true)}
                            className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium text-error-primary hover:bg-error-secondary transition-colors"
                            title={t('detail.issues.resetAll', 'Reset all issues')}
                        >
                            <RefreshCcw01 className="size-3.5" />
                            {t('detail.issues.resetAll', 'Reset issues')}
                        </button>
                    )}
                </div>
                {resolvedCount > 0 && (
                    <button
                        type="button"
                        onClick={() => setShowResolved(!showResolved)}
                        className="text-xs text-tertiary hover:text-primary transition-colors"
                    >
                        {showResolved
                            ? t('detail.issues.hideResolved', 'Hide resolved')
                            : t('detail.issues.showResolved', `Show ${resolvedCount} resolved`, { count: resolvedCount })}
                    </button>
                )}
            </div>

            {/* Reset confirmation dialog */}
            {showResetConfirm && (
                <div className="rounded-xl bg-primary p-4 mb-2 shadow-xs ring-1 ring-secondary">
                    <div className="flex items-start gap-3">
                        <div className="rounded-full bg-error-secondary p-2 shrink-0">
                            <AlertCircle className="size-4 text-error-primary" />
                        </div>
                        <div className="flex-1">
                            <p className="text-sm font-semibold text-primary">
                                {t('detail.issues.resetConfirmTitle', 'Reset all active issues?')}
                            </p>
                            <p className="text-sm text-tertiary mt-1">
                                {t('detail.issues.resetConfirmMessage',
                                    'This will archive {{count}} active issues and reset the risk score to zero. Issues that still persist will be re-created automatically by the next validation cycle.',
                                    { count: activeCount }
                                )}
                            </p>
                            <div className="flex gap-2 mt-3">
                                <button
                                    type="button"
                                    onClick={handleReset}
                                    disabled={resetMutation.isPending}
                                    className="rounded-lg bg-error-solid px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50 transition-colors"
                                >
                                    {resetMutation.isPending
                                        ? t('detail.issues.resetting', 'Resetting...')
                                        : t('detail.issues.confirmReset', 'Confirm reset')}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setShowResetConfirm(false)}
                                    disabled={resetMutation.isPending}
                                    className="rounded-lg border border-secondary px-3 py-1.5 text-sm font-medium text-secondary hover:text-primary hover:border-primary transition-colors"
                                >
                                    {t('common:buttons.cancel', 'Cancel')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <TableCard.Root>
                <Table>
                    <Table.Header>
                        <Table.Head label={t('detail.issues.headers.title', 'Title')} isRowHeader />
                        <Table.Head label={t('detail.issues.headers.issueType', 'Issue Type')} />
                        <Table.Head label={t('detail.issues.headers.blocksShipment', 'Blocks Shipment')} />
                        <Table.Head label={t('detail.issues.headers.severity', 'Severity')} />
                        <Table.Head label={t('detail.issues.headers.status', 'Status')} />
                        <Table.Head label={t('detail.issues.headers.created', 'Created')} />
                        <Table.Head label={t('detail.issues.headers.actions', 'Actions')} />
                    </Table.Header>
                    <Table.Body>
                        {issues.map((issue) => (
                            <Table.Row key={issue.issue_id}>
                                <Table.Cell>
                                    <div className="flex items-center gap-2">
                                        <div>
                                            <div className="font-medium text-primary">{stripContainerRef(issue.title, containerNumber)}</div>
                                            {issue.issue_metadata?.document_name && (
                                                <div className="text-xs text-tertiary mt-0.5 truncate max-w-[280px]" title={issue.issue_metadata.document_name}>
                                                    {issue.issue_metadata.document_name}
                                                </div>
                                            )}
                                        </div>
                                        {issue.description && (
                                            <DialogTrigger>
                                                <Button aria-label="Show issue description" className="flex items-center justify-center p-0.5 text-tertiary hover:text-primary outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring">
                                                    <InfoCircle className="size-4" />
                                                </Button>
                                                <Popover
                                                    aria-label="Issue description"
                                                    placement="top start"
                                                    offset={8}
                                                    className={({ isEntering, isExiting }) =>
                                                        `origin-(--trigger-anchor-point) will-change-transform rounded-lg bg-primary shadow-lg ring-1 ring-secondary ${
                                                            isEntering
                                                                ? 'duration-150 ease-out animate-in fade-in placement-top:slide-in-from-bottom-0.5'
                                                                : isExiting
                                                                  ? 'duration-100 ease-in animate-out fade-out placement-top:slide-out-to-bottom-0.5'
                                                                  : ''
                                                        }`
                                                    }
                                                >
                                                    <div className="p-4 max-w-sm">
                                                        <p className="text-sm text-primary whitespace-pre-wrap">{issue.description}</p>
                                                    </div>
                                                </Popover>
                                            </DialogTrigger>
                                        )}
                                    </div>
                                </Table.Cell>
                                <Table.Cell>
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                        <Badge color="gray" size="sm">
                                            {getIssueTypeLabel(issue.issue_type)}
                                        </Badge>
                                        {issue.issue_source === 'system_detection' && (
                                            <Badge color="brand" size="sm">Auto</Badge>
                                        )}
                                    </div>
                                </Table.Cell>
                                <Table.Cell>
                                    {issue.blocks_shipment ? (
                                        <AlertCircle className="size-5 text-red-600" />
                                    ) : null}
                                </Table.Cell>
                                <Table.Cell>
                                    <Badge color={getSeverityColor(issue.severity)} size="sm">
                                        {issue.severity.charAt(0).toUpperCase() + issue.severity.slice(1)}
                                    </Badge>
                                </Table.Cell>
                                <Table.Cell>
                                    <Badge color={getStatusColor(issue.resolution_status)} size="sm">
                                        {formatStatusLabel(issue.resolution_status)}
                                    </Badge>
                                </Table.Cell>
                                <Table.Cell>
                                    <span className="text-sm text-tertiary">
                                        {formatTimeAgo(issue.created_at)}
                                    </span>
                                </Table.Cell>
                                <Table.Cell>
                                    <Dropdown.Root>
                                        <Dropdown.DotsHorizontalButton />
                                        <Dropdown.Popover className="w-min">
                                            <Dropdown.Menu>
                                                <Dropdown.Item
                                                    id="change-status"
                                                    label={t('detail.issues.actions.changeStatus', 'Change Status')}
                                                    onAction={() => handleChangeStatus(issue)}
                                                />
                                            </Dropdown.Menu>
                                        </Dropdown.Popover>
                                    </Dropdown.Root>
                                </Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>
            </TableCard.Root>

            {/* Change Status Modal */}
            {selectedIssue && (
                <ChangeIssueStatusModal
                    isOpen={showStatusModal}
                    onClose={handleStatusModalClose}
                    issue={selectedIssue}
                    containerId={containerId}
                    containerNumber={containerNumber}
                />
            )}
        </>
    );
}

/**
 * Hook to get active issues count for badge display
 */
export function useActiveIssuesCount(containerId: string | undefined) {
    const { data } = useContainerIssues(containerId);
    const issues = data?.issues || [];
    return issues.filter((issue) => ACTIVE_STATUSES.includes(issue.resolution_status)).length;
}
